import { FC, useEffect, useState, useCallback, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { ClockIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { RootState, AppDispatch } from '../store'
import { tick, showWarning } from '../store/slices/timerSlice'
import { useWebSocket } from '../services/websocket'
import { formatTime, getTimeDisplayProps, shouldShowWarning, getWarningMessage } from '../utils/timer'
import clsx from 'clsx'
import toast from 'react-hot-toast'

interface TimerProps {
  sessionId: string
  className?: string
  showIcon?: boolean
  size?: 'small' | 'medium' | 'large'
}

const Timer: FC<TimerProps> = ({ 
  sessionId, 
  className = '', 
  showIcon = true,
  size = 'medium' 
}) => {
  const dispatch = useDispatch<AppDispatch>()
  const { 
    remainingTimeSeconds, 
    isRunning, 
    isPaused, 
    warningShown, 
    criticalWarningShown 
  } = useSelector((state: RootState) => state.timer)
  
  const { currentSession } = useSelector((state: RootState) => state.test)
  
  const [localTime, setLocalTime] = useState(remainingTimeSeconds)
  const [lastServerSync, setLastServerSync] = useState(Date.now())
  const { requestSync, isConnected, getStatus } = useWebSocket()
  const [showOfflineIndicator, setShowOfflineIndicator] = useState(false)

  // Sync local timer with Redux state
  useEffect(() => {
    setLocalTime(remainingTimeSeconds)
    setLastServerSync(Date.now())
  }, [remainingTimeSeconds])

  // Local countdown ticker
  useEffect(() => {
    console.log('Timer useEffect triggered:', { isRunning, isPaused, localTime })
    if (!isRunning || isPaused) return

    const interval = setInterval(() => {
      // Only dispatch tick to Redux store - let Redux handle the countdown
      dispatch(tick())
    }, 1000)

    return () => clearInterval(interval)
  }, [isRunning, isPaused, dispatch])

  // Periodic server sync (every 15 seconds)
  useEffect(() => {
    if (!isRunning) return

    const syncInterval = setInterval(() => {
      if (isConnected()) {
        requestSync()
      }
    }, 15000)

    return () => clearInterval(syncInterval)
  }, [isRunning, requestSync, isConnected])

  // Handle visibility change to sync timer when tab becomes active
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && isRunning && isConnected()) {
        // Request immediate sync when tab becomes visible
        requestSync()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [isRunning, isConnected, requestSync])

  // Warning notifications - only check when time changes significantly
  useEffect(() => {
    if (!isRunning) return
    
    const warningType = shouldShowWarning(localTime, currentSession?.duration_minutes)
    
    if (warningType === 'critical' && !criticalWarningShown) {
      dispatch(showWarning('critical'))
      const message = getWarningMessage(localTime)
      toast.error(`⚠️ Only ${message}!`, {
        duration: 5000,
        style: {
          background: '#dc2626',
          color: 'white',
        },
      })
    } else if (warningType === 'warning' && !warningShown) {
      dispatch(showWarning('warning'))
      const message = getWarningMessage(localTime)
      toast(`⏰ ${message}`, {
        duration: 4000,
        icon: '⚠️',
        style: {
          background: '#f59e0b',
          color: 'white',
        },
      })
    }
  }, [localTime, isRunning, warningShown, criticalWarningShown, currentSession?.duration_minutes, dispatch])

  // TestPage owns finalization and section navigation. A second submission
  // here could lock the attempt before TestPage flushes pending answers.

  const connectionStatus = getStatus()
  
  useEffect(() => {
    if (!connectionStatus.connected && connectionStatus.reconnectAttempts > 0) {
      const timer = setTimeout(() => {
        setShowOfflineIndicator(true)
      }, 10000)
      return () => clearTimeout(timer)
    }
    setShowOfflineIndicator(false)
  }, [connectionStatus.connected, connectionStatus.reconnectAttempts])
  
  const timeDisplay = getTimeDisplayProps(localTime, remainingTimeSeconds)

  // Size-based styling
  const sizeClasses = {
    small: 'text-sm',
    medium: 'text-lg',
    large: 'text-2xl',
  }

  const iconSizes = {
    small: 'h-4 w-4',
    medium: 'h-5 w-5', 
    large: 'h-6 w-6',
  }

  return (
    <div className={clsx('flex items-center space-x-2', className)}>
      {showIcon && (
        <div className="relative">
          {timeDisplay.critical ? (
            <ExclamationTriangleIcon 
              className={clsx(iconSizes[size], 'text-danger-600')} 
            />
          ) : (
            <ClockIcon 
              className={clsx(iconSizes[size], 'text-gray-500')} 
            />
          )}
          
          {/* Connection indicator */}
          <div 
            className={clsx(
              'absolute -top-1 -right-1 w-2 h-2 rounded-full',
              connectionStatus.connected ? 'bg-success-500' : 'bg-danger-500'
            )}
            title={connectionStatus.connected ? 'Connected' : 'Disconnected'}
          />
        </div>
      )}
      
      <div className="flex flex-col">
        <span 
          className={clsx(
            'font-mono font-bold',
            sizeClasses[size],
            timeDisplay.className
          )}
        >
          {timeDisplay.formatted}
        </span>
        
        {size !== 'small' && (
          <span className="text-xs text-gray-500">
            {isPaused ? 'Paused' : isRunning ? 'Remaining' : 'Stopped'}
          </span>
        )}
      </div>
      
      {/* Status indicators - Show offline only after delay */}
      {(isPaused || showOfflineIndicator) && (
        <div className="flex space-x-1">
          {isPaused && (
            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-warning-100 text-warning-800">
              Paused
            </span>
          )}
          
          {showOfflineIndicator && (
            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-warning-100 text-warning-800">
              Offline Mode
            </span>
          )}
        </div>
      )}
    </div>
  )
}

export default Timer
